<template>
  <div class="card" v-if="loadingHistory">
    <div class="card-body text-center py-10">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-4 text-gray-600">Carregando histórico...</p>
    </div>
  </div>

  <div class="card" v-else-if="medicalRecords.length <= 0">
    <div class="card-body text-center">
      <div class="mt-10">
        <img class="w-50" :src="require('@/assets/images/undraw_collaborating_re_l43g.svg')" alt="image"/>
        <br><br>
        <p class="h3 mt-10 text-gray-600">
          Nenhum Histórico encontrado para o paciente <br> {{schedule?.patient?.patient_name}}
        </p>
      </div>
    </div>
  </div>

  <div v-else>
    <div v-if="!consultationOnly" class="history-filters-card">
      <div class="history-filters-header">
        <ul class="nav mt-5 flex-wrap">
          <li class="nav-item" @click="search = ''">
            <a class="nav-link border-gray-300 border-dashed btn btn-sm btn-color-muted btn-active-color-primary btn-active-light fw-bold fs-7 px-4 me-1"
               :class="{ active: search === '' }"
               data-bs-toggle="tab" href="#" style="border: 1px">
              Todos
            </a>
          </li>
          <li class="nav-item" v-for="recordType in recordTypes" :key="recordType.id_rectype" @click="search = recordType.id_rectype">
            <a class="nav-link border-gray-300 border-dashed btn btn-sm btn-color-muted btn-active-color-primary btn-active-light fw-bold fs-7 px-4 me-1"
               :class="{ active: search === recordType.id_rectype }"
               data-bs-toggle="tab" href="#" style="border: 1px">
              {{recordType.rectype_name}}
              <span class="badge badge-light-primary ms-1" v-if="isTeaRecordType(recordType)">TEA</span>
            </a>
          </li>
        </ul>
        <div class="d-flex justify-content-end align-items-center mt-4 mb-4 gap-2 flex-wrap">
          <div class="d-flex align-items-center gap-2 flex-grow-1 flex-md-grow-0" style="min-width: 280px; max-width: 520px;">
            <div class="position-relative flex-grow-1">
              <i class="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
              <input
                type="search"
                class="form-control form-control-solid form-control-sm ps-10"
                v-model.trim="historyQuery"
                :placeholder="historySearchPlaceholder"
              >
            </div>
            <select
              class="form-select form-select-solid form-select-sm"
              style="width: auto; min-width: 140px;"
              v-model="contentFilter"
            >
              <option
                v-for="option in contentFilterOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <button
            v-if="canOpenProgramsEvolution"
            type="button"
            class="btn btn-flex btn-light-primary m-1"
            title="Evolução de Programas"
            @click="evolutionOpen = true">
            <i class="bi bi-graph-up-arrow"></i> Evolução Programas
          </button>
          <button v-if="schedule?.id_sched === null" class="btn btn-flex btn-primary m-1" data-bs-toggle="modal"
                  title="Novo Registro" :data-bs-target="'#modal_store_medrec'">
            <i class="bi bi-plus-lg"></i> Novo Registro
          </button>
        </div>
      </div>
    </div>

    <div v-if="medicalRecordFiltered.length === 0" class="text-center text-muted py-10">
      Nenhum registro encontrado com esses filtros.
    </div>

    <div class="history-timeline" :class="{ 'consultation-records': consultationOnly }" v-else>
      <div class="timeline-item"
           v-for="(medicalRecord, index) in medicalRecordFiltered"
           :key="medicalRecord.id_medrec"
           :id="'timeline-item-' + medicalRecord.id_medrec">
        <div v-if="!consultationOnly" class="timeline-line-wrapper">
          <div class="timeline-dot" :class="index === 0 ? 'active' : ''"></div>
          <div class="timeline-connector" v-if="index < medicalRecordFiltered.length - 1"></div>
        </div>
        <div v-if="!consultationOnly" class="timeline-date-label">
          <span class="date-day">{{ date(medicalRecord.medrec_date_time).format('DD') }}</span>
          <span class="date-month">{{ date(medicalRecord.medrec_date_time).format('MMM') }}</span>
          <span class="date-year">{{ date(medicalRecord.medrec_date_time).format('YYYY') }}</span>
        </div>
        <div class="timeline-card-wrapper">
          <div class="medical-record-card">
            <component :is="consultationOnly ? 'ConsultationRecordHeader' : 'div'"
                 :class="consultationOnly ? '' : ['card-header-modern cursor-pointer', isCardExpanded(medicalRecord.id_medrec) ? 'active' : 'collapsed']"
                 :medical-record="consultationOnly ? medicalRecord : undefined"
                 :title="consultationOnly ? consultationRecordTitle(medicalRecord) : undefined"
                 :editable="consultationOnly ? canEditMedicalRecord(medicalRecord) : undefined"
                 @click="!consultationOnly && toggleCard(medicalRecord.id_medrec, $event)">
              <div v-if="!consultationOnly" class="card-title">
                <div class="record-type-icon">
                  <span class="svg-icon svg-icon-2 svg-icon-white">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path opacity="0.3" d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="currentColor"/>
                      <path d="M14 2V8H20L14 2ZM18 20H6V4H13V9H18V20Z" fill="currentColor"/>
                    </svg>
                  </span>
                </div>
                <div class="record-header-info">
              <h2 class="record-title">{{ historyRecordTitle(medicalRecord) }}</h2>
              <div class="record-badges">
                <span class="badge badge-light-primary" v-if="isTeaRecordType(medicalRecord.record_type)">TEA/ABA</span>
                <span class="badge badge-light-info" v-if="medicalRecord.shared_readonly">Compartilhado · somente leitura</span>
                <span class="badge badge-light-success" v-else-if="canEditMedicalRecord(medicalRecord)">Liberado para Alteração</span>
                <span class="badge badge-light-danger" v-else>Alteração bloqueada</span>
              </div>
              <div class="record-meta">
                <span v-if="medicalRecord.id_prof != medicalRecord.user?.id_prof">
                  Adicionado em {{date(medicalRecord.created_at).format('DD/MM/YYYY HH:mm')}} em nome de {{medicalRecord.professional?.prof_name}} por {{medicalRecord.user?.user_name}}
                </span>
                <span v-else>
                  Adicionado em {{date(medicalRecord.created_at).format('DD/MM/YYYY HH:mm')}} por {{medicalRecord.professional?.prof_name}}
                </span>
                <div class="symbol symbol-circle symbol-25px ms-2" data-bs-toggle="tooltip" data-bs-boundary="window"
                     data-bs-placement="top" :title="medicalRecord.professional?.prof_name">
                  <img :src="medicalRecord.professional?.prof_photo || require('@/assets/images/no-photo.png')"
                       alt="Foto do profissional"
                       :class="{ 'is-placeholder': !medicalRecord.professional?.prof_photo }"
                       @error="useDefaultPhoto">
                </div>
              </div>
            </div>
          </div>

          <div class="header-actions" @click.stop>
            <RecordAttributesManager :medical-record="medicalRecord"
                                     :schedule="schedule"
                                     :disabled="!canEditMedicalRecord(medicalRecord)"/>
            <button type="button"
                    class="btn btn-icon btn-sm btn-light-primary"
                    title="Imprimir prontuário"
                    :disabled="printingMedrecId === medicalRecord.id_medrec"
                    @click.stop.prevent="printMedicalRecord(medicalRecord)">
              <i class="bi bi-printer"></i>
            </button>
            <button v-if="canShareMedicalRecord(medicalRecord)"
                    class="btn btn-icon btn-sm btn-light-primary"
                    title="Compartilhar"
                    @click="openShareModal(medicalRecord)">
              <i class="bi bi-share"></i>
            </button>
            <button v-if="canOptOutSharedMedicalRecord(medicalRecord)"
                    class="btn btn-icon btn-sm btn-light-warning"
                    title="Deixar de receber"
                    @click="optOutSharedMedicalRecord(medicalRecord)">
              <i class="bi bi-eye-slash"></i>
            </button>
            <button v-if="medicalRecord.allowDelete"
                    class="btn btn-icon btn-sm btn-light-danger"
                    title="Excluir"
                    @click="deleteMedrec(medicalRecord.id_medrec);">
              <i class="bi bi-trash-fill"></i>
            </button>
          </div>

          <div v-if="!consultationOnly" class="card-toolbar" :class="{ 'rotate-180': isCardExpanded(medicalRecord.id_medrec) }">
            <span class="svg-icon svg-icon-1 svg-icon-white">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect opacity="0.5" x="11" y="18" width="13" height="2" rx="1" transform="rotate(-90 11 18)"
                      fill="currentColor"></rect>
                <path
                    d="M11.4343 15.4343L7.25 11.25C6.83579 10.8358 6.16421 10.8358 5.75 11.25C5.33579 11.6642 5.33579 12.3358 5.75 12.75L11.2929 18.2929C11.6834 18.6834 12.3166 18.6834 12.7071 18.2929L18.25 12.75C18.6642 12.3358 18.6642 11.6642 18.25 11.25C17.8358 10.8358 17.1642 10.8358 16.75 11.25L12.5657 15.4343C12.2533 15.7467 11.7467 15.7467 11.4343 15.4343Z"
                    fill="currentColor"></path>
              </svg>
            </span>
          </div>
        </component>

        <div v-if="consultationOnly || isCardExpanded(medicalRecord.id_medrec)" class="card-body-wrapper">
          <div v-if="isLoadingDetails(medicalRecord.id_medrec) || !hasRecordDetails(medicalRecord)" class="card-body text-center py-10">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-4 text-gray-600">Carregando registro...</p>
          </div>
          <div v-else class="card-body">
            <div class="d-flex flex-wrap gap-2 mb-4" v-if="!consultationOnly && historyContentChips(medicalRecord).length">
              <button
                v-for="chip in historyContentChips(medicalRecord)"
                :key="chip.key"
                type="button"
                class="btn btn-sm py-1 px-2"
                :class="chip.btnClass"
                :title="chip.title"
                @click="openHistoryContentTab(medicalRecord, chip.tab)"
              >
                <i :class="chip.icon + ' me-1'"></i>
                {{ chip.label }}
              </button>
            </div>

            <div class="row" v-if="bandAttributes(medicalRecord).length">
              <InputAttendance v-for="attribute in bandAttributes(medicalRecord)"
                               :key="'history-band-' + attribute.id_medrec_attr"
                               @onChange="(attribute) => onAttributeChange(medicalRecord, attribute)"
                               :attribute="attribute"
                               :schedule="schedule"
                               :disabled="!canEditMedicalRecord(medicalRecord)"/>
            </div>

            <component :is="consultationOnly ? 'div' : 'Splitter'" :class="{ 'consultation-content': consultationOnly }">
              <component :is="consultationOnly ? 'div' : 'SplitterPanel'" :size="consultationOnly ? undefined : 40"
                         v-if="!consultationOnly || columnAttributes(medicalRecord).length || specialtyPanelFor(medicalRecord)">
                <div class="row" style="padding-right: 12px;">
                  <component
                      :is="specialtyPanels[specialtyPanelFor(medicalRecord)]"
                      v-if="specialtyPanelFor(medicalRecord)"
                      :attributes="columnAttributes(medicalRecord)"
                      :schedule="schedule"
                      :disabled="!canEditMedicalRecord(medicalRecord)"
                      @onChange="(attribute) => onAttributeChange(medicalRecord, attribute)"
                  />
                  <InputAttendance v-else v-for="attribute in columnAttributes(medicalRecord)"
                                   :key="'history-' + attribute.id_medrec_attr"
                                   :disabled="!canEditMedicalRecord(medicalRecord)"
                                   :schedule="schedule"
                                   @onChange="(attribute) => onAttributeChange(medicalRecord, attribute)"
                                   :attribute="attribute"/>
                </div>
              </component>

              <component :is="consultationOnly ? 'div' : 'SplitterPanel'" v-if="hasSideTabs(medicalRecord)" :size="consultationOnly ? undefined : 60">
                <div style="padding-left: 12px;">
                  <div class="content-card">
                    <div class="modern-nav-header">
                      <div class="nav-desktop-group">
                        <ul class="nav nav-modern">
                          <li
                            v-for="tab in primarySideTabs(medicalRecord)"
                            :key="`desktop-primary-${medicalRecord.id_medrec}-${tab.id}`"
                            class="nav-item"
                          >
                            <button
                                :class="['nav-btn', tab.btnClass, { 'active': isTabActive(medicalRecord.id_medrec, tab.id) }]"
                                data-bs-toggle="tab"
                                :href="'#tab-' + tab.id + '-' + medicalRecord.id_medrec"
                                @click="setActiveTab(medicalRecord.id_medrec, tab.id)"
                                :id="'nav-' + tab.id + '-' + medicalRecord.id_medrec">
                              <span class="nav-icon">
                                <i :class="tab.icon"></i>
                              </span>
                              <span class="nav-text">{{ tab.label }}</span>
                            </button>
                          </li>
                        </ul>
                        <div class="dropdown modern-dropdown" v-if="overflowSideTabs(medicalRecord).length">
                          <button
                            class="dropdown-btn"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            :class="{ active: isOverflowTabActive(medicalRecord) }"
                          >
                            <span class="dropdown-icon">
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <circle cx="5" cy="12" r="2" fill="currentColor"/>
                                <circle cx="12" cy="12" r="2" fill="currentColor"/>
                                <circle cx="19" cy="12" r="2" fill="currentColor"/>
                              </svg>
                            </span>
                          </button>
                          <ul class="dropdown-menu modern-dropdown-menu">
                            <li v-for="tab in overflowSideTabs(medicalRecord)" :key="`desktop-overflow-${medicalRecord.id_medrec}-${tab.id}`">
                              <button
                                class="dropdown-item modern-dropdown-item"
                                :class="{ active: isTabActive(medicalRecord.id_medrec, tab.id) }"
                                @click="switchToTab('tab-' + tab.id + '-' + medicalRecord.id_medrec)"
                              >
                                <span class="dropdown-item-icon">
                                  <i :class="tab.icon"></i>
                                </span>
                                <span class="dropdown-item-text">{{ tab.label }}</span>
                              </button>
                            </li>
                          </ul>
                        </div>
                      </div>

                      <div class="nav-mobile">
                        <ul class="nav nav-modern-mobile">
                          <li
                            v-for="tab in primarySideTabs(medicalRecord)"
                            :key="`mobile-primary-${medicalRecord.id_medrec}-${tab.id}`"
                            class="nav-item"
                          >
                            <button
                                :class="['nav-btn-mobile', tab.btnClass, { 'active': isTabActive(medicalRecord.id_medrec, tab.id) }]"
                                data-bs-toggle="tab"
                                :href="'#tab-' + tab.id + '-' + medicalRecord.id_medrec"
                                @click="setActiveTab(medicalRecord.id_medrec, tab.id)"
                                :id="'nav-mobile-' + tab.id + '-' + medicalRecord.id_medrec">
                              <span class="nav-icon-mobile">
                                <i :class="tab.icon"></i>
                              </span>
                              <span class="nav-text-mobile">{{ tab.label }}</span>
                            </button>
                          </li>
                        </ul>

                        <div class="dropdown modern-dropdown" v-if="overflowSideTabs(medicalRecord).length">
                          <button
                            class="dropdown-btn"
                            style="margin-left: 10px;"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            :class="{ active: isOverflowTabActive(medicalRecord) }"
                          >
                            <span class="dropdown-icon">
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <circle cx="5" cy="12" r="2" fill="currentColor"/>
                                <circle cx="12" cy="12" r="2" fill="currentColor"/>
                                <circle cx="19" cy="12" r="2" fill="currentColor"/>
                              </svg>
                            </span>
                          </button>
                          <ul class="dropdown-menu modern-dropdown-menu">
                            <li v-for="tab in overflowSideTabs(medicalRecord)" :key="`mobile-overflow-${medicalRecord.id_medrec}-${tab.id}`">
                              <button
                                class="dropdown-item modern-dropdown-item"
                                :class="{ active: isTabActive(medicalRecord.id_medrec, tab.id) }"
                                @click="switchToTab('tab-' + tab.id + '-' + medicalRecord.id_medrec)"
                              >
                                <span class="dropdown-item-icon">
                                  <i :class="tab.icon"></i>
                                </span>
                                <span class="dropdown-item-text">{{ tab.label }}</span>
                              </button>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div class="card-body" style="padding-top: 0.5rem; padding-bottom: 0.5rem;">
                      <div class="tab-content">
                        <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'pre-schedule') }]"
                             :id="'tab-pre-schedule-' + medicalRecord.id_medrec" role="tabpanel"
                             v-if="hasPreScheduleAnswer(medicalRecord)">
                          <PreScheduleAnswerView :answer="medicalRecord.schedule.pre_schedule_answer"/>
                        </div>
                        <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'prescription') }]"
                             :id="'tab-prescription-' + medicalRecord.id_medrec" role="tabpanel"
                             v-if="medicalRecord.record_type?.allow_prescription">
                          <Prescription :readonly="!medicalRecord.allowUpdate" :medical-record="medicalRecord" :schedule="schedule" :presc-types="prescTypes" :prescription-protocols="prescriptionProtocols" :drug-admins="drugsAdmins" :certifiers="certifiers" :key="medicalRecord?.id_medrec"/>
                        </div>
                        <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'exam') }]"
                             :id="'tab-exam-' + medicalRecord.id_medrec" role="tabpanel"
                             v-if="medicalRecord.record_type?.allow_exam_request">
                          <ExamRequest :readonly="!medicalRecord.allowUpdate" :medical-record="medicalRecord" :schedule="schedule" :certifiers="certifiers" :key="medicalRecord?.id_medrec"/>
                        </div>
                        <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'programs') }]"
                             :id="'tab-programs-' + medicalRecord.id_medrec" role="tabpanel"
                             v-if="medicalRecord.record_type?.allow_programs">
                          <Programs class="history-programs"
                            :medical-record="medicalRecord"
                            :schedule="schedule"
                            :key="'programs-' + medicalRecord?.id_medrec"/>
                        </div>
                        <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'document') }]"
                             :id="'tab-document-' + medicalRecord.id_medrec" role="tabpanel"
                             v-if="medicalRecord.record_type?.allow_document">
                          <Document :readonly="!medicalRecord.allowUpdate" :medical-record="medicalRecord" :schedule="schedule" :document-templates="documentTemplates" :certifiers="certifiers" :key="medicalRecord?.id_medrec"/>
                        </div>
                        <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'health-data') }]"
                             :id="'tab-health-data-' + medicalRecord.id_medrec" role="tabpanel"
                             v-if="isTabActive(medicalRecord.id_medrec, 'health-data')">
                          <HealthData :readonly="!medicalRecord.allowUpdate" :medical-record="medicalRecord" :schedule="schedule" :health-indicator-groups="healthIndicatorGroups" :auto-load="true" :key="medicalRecord?.id_medrec" ref="healthDataRef"/>
                        </div>
                        <div :class="['tab-pane', 'fade', { 'show active': isTabActive(medicalRecord.id_medrec, 'attachment') }]"
                             :id="'tab-attachment-' + medicalRecord.id_medrec" role="tabpanel">
                          <Attachment :readonly="!medicalRecord.allowUpdate" :medical-record="medicalRecord" :schedule="schedule" :key="medicalRecord?.id_medrec"/>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="medicalRecord.record_type?.allow_cid && medicalRecord?.id_sched" class="cid-card">
                    <div class="cid-header">
                      <div class="cid-title">
                        <span class="cid-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path opacity="0.3" d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="currentColor"/>
                            <path d="M14 2V8H20L14 2ZM18 20H6V4H13V9H18V20Z" fill="currentColor"/>
                          </svg>
                        </span>
                        <h3 class="cid-title-text">CID</h3>
                      </div>
                    </div>
                    <div class="cid-body">
                      <Cid :readonly="!medicalRecord.allowUpdate" :medical-record="medicalRecord" :schedule="schedule" :key="'cid-' + medicalRecord?.id_medrec"/>
                    </div>
                  </div>
                </div>
              </component>
            </component>
          </div>
        </div>
      </div><!-- .medical-record-card -->
      </div><!-- .timeline-card-wrapper -->
      </div><!-- .timeline-item -->
    </div>
  </div>

  <Teleport to="body">
<div class="modal fade" tabindex="-1" id="modal_store_medrec">
    <div class="modal-dialog modal-lg">
      <form method="post" @submit.prevent="submitFormStore">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Novo Registro</h5>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" data-bs-dismiss="modal" aria-label="Fechar">
              <i class="bi bi-x-lg"></i>
            </div>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-md-12 mb-6 mt-4">
                <label for="rectype_store" class="form-label required">Tipo de Registro</label>
                <select class="form-control form-select required" id="rectype_store"
                        aria-label="Selecione o tipo de registro" v-model="formStore.id_rectype">
                  <option value="" selected>--Nenhum--</option>
                  <option :value="recordType.id_rectype" v-for="recordType in recordTypes">
                    {{ recordType.rectype_name }}
                  </option>
                </select>
                <small class="text-danger" v-if="formStoreErrors.id_rectype">{{ formStoreErrors.id_rectype[0] }}</small>
              </div>
              <div class="col-md-12 mb-6 mt-4">
                <label for="medrec_date_time_store" class="required form-label required">Data para Registro</label>
                <input class="form-control form-control-solid"
                       placeholder="Data para Registro"
                       type="text"
                       :class="formStoreErrors.medrec_date_time ? 'is-invalid' : ''" v-mask="'##/##/####'"
                       v-model="formStore.medrec_date_time_fmt" id="medrec_date_time_store" required>
                <small class="text-danger" v-if="formStoreErrors.medrec_date_time">{{ formStoreErrors.medrec_date_time[0] }}</small>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Fechar</button>
            <button type="submit" class="btn btn-primary">Cadastrar</button>
          </div>
        </div>
      </form>
    </div>
  </div>
  </Teleport>

  <PdfViewer id="modal_pdf_viewer_medical_record_history"
             :pdf-source="pdfSource"
             :file-name="pdfFileName"
             :signed="pdfSigned"/>

  <MedicalRecordPrinter
    ref="medicalRecordPrinter"
    id="history"
    :schedule="schedule"
    :certifiers="certifiers"
    @printed="onMedicalRecordPrinted"/>

  <ModalShareMedicalRecord ref="shareModal" :medical-record="shareMedicalRecord"/>

  <ProgramsEvolutionDrawer
    v-if="canOpenProgramsEvolution"
    :is-open="evolutionOpen"
    :professional-id="schedule?.id_prof"
    :patient-id="schedule?.id_patient || schedule?.patient?.id_patient"
    :place-id="schedule?.id_place"
    @close="evolutionOpen = false"
  />
</template>

<script>
import ConsultationRecordHeader from '../shared/ConsultationRecordHeader.vue';
import moment from "../../../helpers/date";
import http from "../../../helpers/http";
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import InputAttendance from '../shared/Input';
import Prescription from "./Prescription";
import ExamRequest from "./ExamRequest";
import {mask} from 'vue-the-mask';
import Alert from "../../../helpers/alert";
import Document from "./Document";
import Cid from "./Cid";
import HealthData from "./HealthData";
import Attachment from "./Attachment";
import Programs from "./Programs";
import Splitter from "primevue/splitter";
import SplitterPanel from "primevue/splitterpanel";
import OphthalmologyPanel from "../shared/OphthalmologyPanel.vue";
import OdontologyPanel from "../shared/OdontologyPanel.vue";
import RecordAttributesManager from "../shared/RecordAttributesManager.vue";
import PdfViewer from "../shared/PdfViewer";
import MedicalRecordPrinter from "../shared/MedicalRecordPrinter.vue";
import {showMedicalRecordPdf} from "../shared/printMedicalRecord";
import PreScheduleAnswerView from "../../pre_schedule/PreScheduleAnswerView.vue";
import {resolveSpecialtyPanel, splitFullWidthAttributes, PANEL_ODONTOLOGY, PANEL_OPHTHALMOLOGY} from "../shared/specialty-panel";
import {medicalRecordHeaderTitle, resolveRecordSchedule} from "../shared/recordHeader";
import ModalShareMedicalRecord from "../../medical_record_settings/ModalShareMedicalRecord.vue";
import ProgramsEvolutionDrawer from "../shared/ProgramsEvolutionDrawer.vue";
import permission from "../../../helpers/permission";
import { hasPlanFeature } from "../../../helpers/platformFeature";

export default {
  name: "History",
  components: {
    ConsultationRecordHeader,
    Document,
    ExamRequest,
    Prescription,
    InputAttendance,
    Cid,
    HealthData,
    Attachment,
    Programs,
    Splitter,
    SplitterPanel,
    OphthalmologyPanel,
    OdontologyPanel,
    PreScheduleAnswerView,
    ModalShareMedicalRecord,
    ProgramsEvolutionDrawer,
    RecordAttributesManager,
    PdfViewer,
    MedicalRecordPrinter,
  },
  directives: {
    mask,
  },
  props: {
    schedule: Object,
    recordTypes: Object,
    examProtocols: Array,
    prescriptionProtocols: Array,
    prescTypes: Array,
    drugsAdmins: Array,
    documentTemplates: Array,
    healthIndicatorGroups: Array,
    certifiers: Array,
    selectedDate: String,
    consultationOnly: Boolean,
  },
  data() {
    return {
      search: '',
      contentFilter: 'all',
      historyQuery: '',
      evolutionOpen: false,
      activeTabs: {},
      expandedCards: {},
      specialtyPanels: {
        [PANEL_ODONTOLOGY]: 'OdontologyPanel',
        [PANEL_OPHTHALMOLOGY]: 'OphthalmologyPanel',
      },
      medicalRecords: [],
      loadingHistory: true,
      loadingDetails: {},
      shareMedicalRecord: null,
      pdfSource: '',
      pdfFileName: '',
      pdfSigned: false,
      printingMedrecId: null,
      formStore: {
        id_rectype: '',
        id_prof: '',
        id_patient: '',
        id_place: '',
        medrec_date_time: '',
        medrec_date_time_fmt: this.date().format('DD/MM/YYYY'),
      },
      formStoreErrors: [],
      contentFilterOptions: [
        { value: 'all', label: 'Todos' },
        { value: 'documents', label: 'Documentos' },
        { value: 'prescriptions', label: 'Prescrição' },
        { value: 'exams', label: 'Exames' },
        { value: 'attachments', label: 'Anexos' },
        { value: 'cids', label: 'CID' },
        { value: 'programs', label: 'Programas' },
      ],
    }
  },
  watch: {
    selectedDate() {
      if (this.consultationOnly) this.$nextTick(this.initializeActiveTabs);
    },
    schedule(newSchedule, oldSchedule) {
      if (newSchedule?.availability == null) {
        return;
      }

      if (
        newSchedule?.id_patient === oldSchedule?.id_patient
        && newSchedule?.id_prof === oldSchedule?.id_prof
        && newSchedule?.id_sched === oldSchedule?.id_sched
      ) {
        return;
      }

      this.getHistoryMedicalRecord();
    },
    medicalRecords: {
      handler(newRecords, oldRecords) {
        if (newRecords === oldRecords) {
          return;
        }

        this.$nextTick(() => {
          this.initializeActiveTabs();
        });
      },
    },
  },
  computed: {
    canOpenProgramsEvolution() {
      const patientId = this.schedule?.id_patient || this.schedule?.patient?.id_patient;
      const professionalId = this.schedule?.id_prof;
      if (!patientId || !professionalId) {
        return false;
      }
      const types = Array.isArray(this.recordTypes)
        ? this.recordTypes
        : Object.values(this.recordTypes || {});
      return types.some((type) => type?.allow_programs)
        || (this.medicalRecords || []).some((record) => record.record_type?.allow_programs || record.program_entries?.length);
    },
    historySearchPlaceholder() {
      const placeholders = {
        all: 'Buscar (ex.: PTI, receita...)',
        documents: 'Buscar documento (ex.: PTI)',
        prescriptions: 'Filtrar registros com prescrição',
        exams: 'Filtrar registros com exame',
        attachments: 'Filtrar registros com anexo',
        cids: 'Filtrar registros com CID',
        programs: 'Filtrar registros com programas',
      };
      return placeholders[this.contentFilter] || placeholders.all;
    },
    medicalRecordFiltered() {
      if (this.consultationOnly) {
        return this.medicalRecords.filter(record => moment(record.medrec_date_time).isSame(this.selectedDate, 'day'));
      }
      let records = this.medicalRecords;

      if (this.search !== '') {
        records = records.filter(medicalRecord => medicalRecord.id_rectype === this.search);
      }

      if (this.contentFilter !== 'all') {
        records = records.filter((medicalRecord) => this.recordHasContent(medicalRecord, this.contentFilter));
      }

      if (this.historyQuery) {
        const query = this.historyQuery.toLowerCase();
        records = records.filter((medicalRecord) => this.recordMatchesQuery(medicalRecord, query));
      }

      if (this.selectedDate) {
        records = records.filter(medicalRecord => {
          return moment(medicalRecord.medrec_date_time).format('DD/MM/YYYY') === moment(this.selectedDate).format('DD/MM/YYYY');
        });
      }

      return records;
    },
    userAuth() {
      return this.$store.state.auth.user;
    },
    place() {
      return this.$store.state.auth.place;
    }
  },
  mounted() {
    this.getHistoryMedicalRecord();
    this.initializeActiveTabs();
  },
  methods: {
    date: moment,
    isCardExpanded(id) {
      return !!this.expandedCards[id];
    },
    useDefaultPhoto(event) {
      event.target.onerror = null;
      event.target.src = require('@/assets/images/no-photo.png');
      event.target.classList.add('is-placeholder');
    },
    toggleCard(id, event) {
      if (event?.target?.closest('.header-actions')) {
        return;
      }

      const expanding = !this.isCardExpanded(id);
      this.expandedCards = Object.assign({}, this.expandedCards, {
        [id]: expanding
      });

      if (expanding) {
        const record = this.medicalRecords.find((item) => item.id_medrec === id);
        if (record) {
          this.loadRecordDetails(record);
        }
      }
    },
    expandCard(id) {
      this.expandedCards = Object.assign({}, this.expandedCards, {
        [id]: true
      });
    },
    initializeActiveTabs() {
      const records = this.consultationOnly ? this.medicalRecordFiltered : this.medicalRecords;
      if (records && Array.isArray(records) && records.length > 0) {
        const newActiveTabs = { ...this.activeTabs };
        const newExpanded = { ...this.expandedCards };

        if (this.consultationOnly) {
          records.forEach(record => { newExpanded[record.id_medrec] = true; });
        } else if (!Object.values(newExpanded).some(v => v)) {
          newExpanded[records[0].id_medrec] = true;
        }

        records.forEach(record => {
          if (!newActiveTabs[record.id_medrec]) {
            newActiveTabs[record.id_medrec] = this.defaultTabForRecord(record);
          } else if (!this.isTabAllowed(record, newActiveTabs[record.id_medrec])) {
            newActiveTabs[record.id_medrec] = this.defaultTabForRecord(record);
          }
        });
        this.activeTabs = newActiveTabs;
        this.expandedCards = newExpanded;

        if (this.consultationOnly) {
          records.forEach(record => this.loadRecordDetails(record));
          return;
        }
        const expandedId = Object.keys(newExpanded).find((id) => newExpanded[id]);
        if (expandedId) {
          const record = records.find((item) => String(item.id_medrec) === String(expandedId));
          if (record) {
            this.loadRecordDetails(record);
          }
        }
      }
    },
    defaultTabForRecord(record) {
      if (this.hasPreScheduleAnswer(record)) {
        return 'pre-schedule';
      }
      if (record.record_type?.allow_prescription) {
        return 'prescription';
      }
      if (record.record_type?.allow_exam_request) {
        return 'exam';
      }
      if (record.record_type?.allow_programs) {
        return 'programs';
      }
      if (record.record_type?.allow_document) {
        return 'document';
      }
      return 'health-data';
    },
    isTabAllowed(record, tabName) {
      if (tabName === 'pre-schedule') {
        return this.hasPreScheduleAnswer(record);
      }
      if (tabName === 'prescription') {
        return !!record.record_type?.allow_prescription;
      }
      if (tabName === 'exam') {
        return !!record.record_type?.allow_exam_request;
      }
      if (tabName === 'document') {
        return !!record.record_type?.allow_document;
      }
      if (tabName === 'programs') {
        return !!record.record_type?.allow_programs;
      }
      return ['health-data', 'attachment', 'cid'].includes(tabName);
    },
    hasSideTabs(medicalRecord) {
      return medicalRecord.record_type?.allow_prescription
        || medicalRecord.record_type?.allow_exam_request
        || medicalRecord.record_type?.allow_document
        || medicalRecord.record_type?.allow_programs
        || this.hasPreScheduleAnswer(medicalRecord);
    },
    availableSideTabs(medicalRecord) {
      const tabs = [];
      if (this.hasPreScheduleAnswer(medicalRecord)) {
        tabs.push({
          id: 'pre-schedule',
          label: 'Pré-Agendamento',
          icon: 'bi bi-clipboard-check',
          btnClass: 'nav-btn-primary',
        });
      }
      if (medicalRecord.record_type?.allow_prescription) {
        tabs.push({
          id: 'prescription',
          label: 'Prescrição',
          icon: 'bi bi-file-earmark-medical',
          btnClass: 'nav-btn-primary',
        });
      }
      if (medicalRecord.record_type?.allow_exam_request) {
        tabs.push({
          id: 'exam',
          label: 'Exame',
          icon: 'bi bi-clipboard2-pulse',
          btnClass: 'nav-btn-secondary',
        });
      }
      if (medicalRecord.record_type?.allow_programs) {
        tabs.push({
          id: 'programs',
          label: 'Programas',
          icon: 'bi bi-bullseye',
          btnClass: 'nav-btn-primary',
        });
      }
      tabs.push({
        id: 'health-data',
        label: 'Dados de Saúde',
        icon: 'bi bi-heart-pulse',
        btnClass: 'nav-btn-success',
      });
      if (medicalRecord.record_type?.allow_document) {
        tabs.push({
          id: 'document',
          label: 'Documentos',
          icon: 'bi bi-file-earmark-text',
          btnClass: 'nav-btn-primary',
        });
      }
      tabs.push({
        id: 'attachment',
        label: 'Anexos',
        icon: 'bi bi-paperclip',
        btnClass: 'nav-btn-primary',
      });
      return tabs;
    },
    primarySideTabs(medicalRecord) {
      return this.availableSideTabs(medicalRecord).slice(0, 3);
    },
    overflowSideTabs(medicalRecord) {
      return this.availableSideTabs(medicalRecord).slice(3);
    },
    isOverflowTabActive(medicalRecord) {
      const active = this.activeTabs[medicalRecord.id_medrec];
      return this.overflowSideTabs(medicalRecord).some((tab) => tab.id === active);
    },
    isTabActive(medicalRecordId, tabName) {
      return this.activeTabs[medicalRecordId] === tabName;
    },
    setActiveTab(medicalRecordId, tabName) {
      this.activeTabs = Object.assign({}, this.activeTabs, {
        [medicalRecordId]: tabName
      });
    },
    switchToTab(tabId) {
      const medicalRecordId = tabId.split('-').pop();
      const tabName = tabId.replace('tab-', '').replace(`-${medicalRecordId}`, '');

      this.setActiveTab(medicalRecordId, tabName);

      this.$forceUpdate();

      this.$nextTick(() => {
        document.querySelectorAll(`[id*="${medicalRecordId}"]`).forEach(btn => {
          if (btn.classList.contains('nav-btn') || btn.classList.contains('nav-btn-mobile')) {
            btn.classList.remove('active');
          }
        });

        document.querySelectorAll(`[id*="${medicalRecordId}"]`).forEach(pane => {
          if (pane.classList.contains('tab-pane')) {
            pane.classList.remove('show', 'active');
          }
        });

        const targetPane = document.getElementById(tabId);
        if (targetPane) {
          targetPane.classList.add('show', 'active');
        }

        const desktopLink = document.getElementById(`nav-${tabName}-${medicalRecordId}`);
        const mobileLink = document.getElementById(`nav-mobile-${tabName}-${medicalRecordId}`);

        if (desktopLink) {
          desktopLink.classList.add('active');
        }
        if (mobileLink) {
          mobileLink.classList.add('active');
        }
      });
    },
    bandAttributes(medicalRecord) {
      return splitFullWidthAttributes(medicalRecord?.attributes).band;
    },
    columnAttributes(medicalRecord) {
      return splitFullWidthAttributes(medicalRecord?.attributes).rest;
    },
    documentsOf(medicalRecord) {
      return Array.isArray(medicalRecord?.documents) ? medicalRecord.documents : [];
    },
    prescriptionsOf(medicalRecord) {
      return Array.isArray(medicalRecord?.prescriptions) ? medicalRecord.prescriptions : [];
    },
    examsOf(medicalRecord) {
      return Array.isArray(medicalRecord?.exam_requests)
        ? medicalRecord.exam_requests
        : (Array.isArray(medicalRecord?.examRequests) ? medicalRecord.examRequests : []);
    },
    attachmentsOf(medicalRecord) {
      return Array.isArray(medicalRecord?.attachments) ? medicalRecord.attachments : [];
    },
    cidsOf(medicalRecord) {
      return Array.isArray(medicalRecord?.cids) ? medicalRecord.cids : [];
    },
    programEntriesCount(medicalRecord) {
      return Number(medicalRecord?.program_entries_count || 0);
    },
    recordHasContent(medicalRecord, contentFilter) {
      switch (contentFilter) {
        case 'documents':
          return this.documentsOf(medicalRecord).length > 0;
        case 'prescriptions':
          return this.prescriptionsOf(medicalRecord).length > 0;
        case 'exams':
          return this.examsOf(medicalRecord).length > 0;
        case 'attachments':
          return this.attachmentsOf(medicalRecord).length > 0;
        case 'cids':
          return this.cidsOf(medicalRecord).length > 0;
        case 'programs':
          return this.programEntriesCount(medicalRecord) > 0;
        default:
          return true;
      }
    },
    recordMatchesQuery(medicalRecord, query) {
      const haystacks = [];

      if (this.contentFilter === 'all' || this.contentFilter === 'documents') {
        this.documentsOf(medicalRecord).forEach((document) => {
          haystacks.push(document.document_name);
        });
      }

      if (this.contentFilter === 'all' || this.contentFilter === 'prescriptions') {
        this.prescriptionsOf(medicalRecord).forEach((prescription) => {
          haystacks.push(prescription.presc_name, prescription.prescription_type?.presctype_name);
        });
      }

      if (this.contentFilter === 'all' || this.contentFilter === 'exams') {
        this.examsOf(medicalRecord).forEach((examRequest) => {
          haystacks.push(examRequest.exam_request_name, examRequest.title);
        });
      }

      if (this.contentFilter === 'all' || this.contentFilter === 'attachments') {
        this.attachmentsOf(medicalRecord).forEach((attachment) => {
          haystacks.push(attachment.attachment_name, attachment.file?.file_name, attachment.file?.original_name);
        });
      }

      if (this.contentFilter === 'all' || this.contentFilter === 'cids') {
        this.cidsOf(medicalRecord).forEach((cid) => {
          haystacks.push(cid.cid_code, cid.cid_description, cid.cid_name);
        });
      }

      haystacks.push(
        medicalRecord.record_type?.rectype_name,
        medicalRecord.professional?.prof_name,
      );

      return haystacks.some((value) => String(value || '').toLowerCase().includes(query));
    },
    historyContentChips(medicalRecord) {
      if (this.contentFilter === 'prescriptions') {
        const count = this.prescriptionsOf(medicalRecord).length;
        return count
          ? [{
            key: `presc-${medicalRecord.id_medrec}`,
            label: `${count} prescrição(ões)`,
            title: 'Abrir prescrições',
            tab: 'prescription',
            icon: 'bi bi-capsule',
            btnClass: 'btn-light-primary',
          }]
          : [];
      }

      if (this.contentFilter === 'exams') {
        const count = this.examsOf(medicalRecord).length;
        return count
          ? [{
            key: `exam-${medicalRecord.id_medrec}`,
            label: `${count} exame(s)`,
            title: 'Abrir exames',
            tab: 'exam',
            icon: 'bi bi-clipboard2-pulse',
            btnClass: 'btn-light-warning',
          }]
          : [];
      }

      if (this.contentFilter === 'attachments') {
        const count = this.attachmentsOf(medicalRecord).length;
        return count
          ? [{
            key: `att-${medicalRecord.id_medrec}`,
            label: `${count} anexo(s)`,
            title: 'Abrir anexos',
            tab: 'attachment',
            icon: 'bi bi-paperclip',
            btnClass: 'btn-light-dark',
          }]
          : [];
      }

      if (this.contentFilter === 'cids') {
        return this.cidsOf(medicalRecord).slice(0, 5).map((cid) => ({
          key: `cid-${medicalRecord.id_medrec}-${cid.id_cid || cid.cid_code}`,
          label: cid.cid_code || cid.cid_name || 'CID',
          title: cid.cid_description || cid.cid_name || 'Abrir CID',
          tab: 'cid',
          icon: 'bi bi-bookmark',
          btnClass: 'btn-light-danger',
        }));
      }

      if (this.contentFilter === 'programs') {
        const count = this.programEntriesCount(medicalRecord);
        return [{
          key: `program-${medicalRecord.id_medrec}`,
          label: count > 0 ? `${count} entrada(s)` : 'Programas habilitados',
          title: 'Ver registro',
          tab: 'register',
          icon: 'bi bi-bullseye',
          btnClass: 'btn-light-success',
        }];
      }

      return this.documentsOf(medicalRecord).map((document) => ({
        key: `doc-${document.id_document}`,
        label: document.document_name || (`Documento #${document.id_document}`),
        title: `Abrir documento #${document.id_document}`,
        tab: 'document',
        icon: 'bi bi-file-earmark-text',
        btnClass: 'btn-light-info',
      }));
    },
    openHistoryContentTab(medicalRecord, tab) {
      this.expandCard(medicalRecord.id_medrec);
      this.loadRecordDetails(medicalRecord);
      this.setActiveTab(medicalRecord.id_medrec, tab);

      this.$nextTick(() => {
        const item = document.getElementById(`timeline-item-${medicalRecord.id_medrec}`);
        if (item) {
          item.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        this.switchToTab(`tab-${tab}-${medicalRecord.id_medrec}`);
      });
    },
    hasRecordDetails(medicalRecord) {
      return Array.isArray(medicalRecord?.attributes);
    },
    isLoadingDetails(id) {
      return !!this.loadingDetails[id];
    },
    loadRecordDetails(medicalRecord) {
      if (!medicalRecord?.id_medrec || this.hasRecordDetails(medicalRecord)) {
        return Promise.resolve(medicalRecord);
      }

      const id = medicalRecord.id_medrec;
      if (this.loadingDetails[id]) {
        return this.loadingDetails[id];
      }

      const request = http.get(
        `/professionals/${this.schedule?.id_prof}/patients/${this.schedule.id_patient}/medical-records/${id}`
      ).then((response) => {
        const idx = this.medicalRecords.findIndex((item) => item.id_medrec === id);
        if (idx !== -1) {
          const merged = Object.assign({}, this.medicalRecords[idx], response.data);
          this.medicalRecords.splice(idx, 1, merged);
          return merged;
        }

        return response.data;
      }).catch((e) => {
        Alert.toastError(e.response?.data?.message ?? e.response?.data ?? 'Erro ao carregar o registro.');
        this.expandedCards = Object.assign({}, this.expandedCards, { [id]: false });
      }).finally(() => {
        const next = Object.assign({}, this.loadingDetails);
        delete next[id];
        this.loadingDetails = next;
      });

      this.loadingDetails = Object.assign({}, this.loadingDetails, { [id]: request });

      return request;
    },
    getHistoryMedicalRecord() {
      if (this.schedule === null) {
        this.loadingHistory = false;
        return Promise.resolve();
      }

      this.loadingHistory = true;
      this.loadingDetails = {};
      this.expandedCards = {};

      return http.get(`/professionals/${this.schedule?.id_prof}/patients/${this.schedule.id_patient}/medical-records`, {params:{ current_schedule_id: this.schedule.id_sched}}).then((response) => {
        this.medicalRecords = response.data;
        return response;
      }).finally(() => {
        this.loadingHistory = false;
      });
    },
    getRecordTypes() {
      if (!this.professionalId) {
        return;
      }

      http.get(`/professionals/${this.professionalId}/record-types/actives`).then((response) => {
        this.recordTypes = response.data;
      });
    },
    submitFormStore() {
      this.formStoreErrors = [];
      this.formStore.id_patient = this.schedule.id_patient;
      this.formStore.id_prof = this.schedule.id_prof;
      this.formStore.id_place = this.place.id_place;
      this.formStore.medrec_date_time = moment(this.formStore.medrec_date_time_fmt, 'DD/MM/YYYY').hour(12).minute(0).second(0).format('YYYY-MM-DD HH:mm:ss');

      http.post(`/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records`, this.formStore).then((response) => {
        bootstrap.Modal.getInstance(document.getElementById('modal_store_medrec')).toggle();
        Alert.toastSuccess("Registro cadastrado com sucesso.");
        this.formStore = {
          id_rectype: '',
          id_prof: '',
          id_patient: '',
          medrec_date_time: '',
        };
        const newId = response.data.id_medrec;
        this.getHistoryMedicalRecord().then( () => {
          this.expandedCards = { [newId]: true };
          this.$nextTick(() => {
            setTimeout(() => {
              const element = document.getElementById(`timeline-item-${newId}`);
              if (element != null) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 100);
          });
        });
      }).catch(e => {
        Alert.toastError(e.response.data.message??e.response.data);
        if (e.response.data.errors) {
          this.formStoreErrors = e.response.data.errors;
        }
      });
    },
    canEditMedicalRecord(medicalRecord) {
      return !medicalRecord.shared_readonly && medicalRecord.allowUpdate;
    },
    canShareMedicalRecord(medicalRecord) {
      if (!hasPlanFeature('prontuario.record_sharing')) {
        return false;
      }
      if (!permission.has('Share Medical Record')) {
        return false;
      }
      return !medicalRecord.shared_readonly && Number(medicalRecord.id_prof) === Number(this.schedule?.id_prof);
    },
    canOptOutSharedMedicalRecord(medicalRecord) {
      if (!hasPlanFeature('prontuario.record_sharing')) {
        return false;
      }
      return !!medicalRecord.shared_readonly;
    },
    openShareModal(medicalRecord) {
      this.shareMedicalRecord = medicalRecord;
      this.$nextTick(() => {
        this.$refs.shareModal?.open?.();
      });
    },
    optOutSharedMedicalRecord(medicalRecord) {
      Swal.fire({
        title: 'Deixar de receber compartilhamentos?',
        text: 'Você deixará de ver os prontuários deste paciente compartilhados por este profissional.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, deixar de receber',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-warning',
          cancelButton: 'btn btn-secondary',
        },
      }).then((result) => {
        if (!result.isConfirmed) {
          return;
        }
        http.post(`/places/${this.place.id_place}/medical-records/${medicalRecord.id_medrec}/shares/opt-out`)
          .then(() => {
            Alert.toastSuccess('Compartilhamento encerrado.');
            this.getHistoryMedicalRecord();
          })
          .catch((e) => {
            Alert.toastError(e.response?.data?.message ?? e.response?.data ?? 'Erro ao encerrar.');
          });
      });
    },
    onAttributeChange(medicalRecord, attribute) {
      if (!this.canEditMedicalRecord(medicalRecord)) {
        return;
      }

      this.saveAttribute(attribute);
    },
    saveAttribute(attribute) {
      let url = `/professionals/${this.schedule?.id_prof}/patients/${this.schedule?.id_patient}/medical-records/${attribute.id_medrec}/attributes/${attribute.id_medrec_attr}`;
      http.put(url, {'text': attribute.text, 'text_compl': attribute.text_compl}).then((_) => {
      }).catch((_) => {
        Alert.toastError("Erro ao salvar informação.");
      });
    },
    deleteMedrec(medrecId) {
      Swal.fire({
        title: 'Heeyy',
        text: "Tem certeza que deseja remover o registro?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, remover!',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: 'btn btn-secundary'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          http.delete(`/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/medical-records/${medrecId}`).then(_ => {
            this.getHistoryMedicalRecord();
            Alert.toastSuccess("Registro excluÍdo com sucesso.");
          }).catch(e => {
            Alert.toastError(e.response.data);
          })
        }
      });
    },
    hasPreScheduleAnswer(medicalRecord) {
      return medicalRecord?.id_sched &&
             medicalRecord?.schedule &&
             medicalRecord?.schedule?.pre_schedule_answer &&
             medicalRecord?.schedule?.pre_schedule_answer?.answered_at;
    },
    getPreScheduleAnswer(medicalRecord) {
      if (!this.hasPreScheduleAnswer(medicalRecord)) {
        return null;
      }
      return medicalRecord.schedule.pre_schedule_answer;
    },
    getPreScheduleQuestions(medicalRecord) {
      const answer = this.getPreScheduleAnswer(medicalRecord);
      if (!answer?.form?.questions) {
        return [];
      }
      return [...answer.form.questions].sort((a, b) => a.question_order - b.question_order);
    },
    getPreScheduleFormName(medicalRecord) {
      const answer = this.getPreScheduleAnswer(medicalRecord);
      return answer?.form?.form_name || '-';
    },
    getAnswerValue(questionId, answers) {
      if (!answers || !questionId) {
        return null;
      }
      return answers[questionId] || null;
    },
    formatPreScheduleAnswer(question, answerValue) {
      if (!answerValue) {
        return 'Não respondido';
      }

      if (question.question_type === 'checkbox' && Array.isArray(answerValue)) {
        return answerValue.join(', ');
      }

      if (question.question_type === 'radio' && question.options) {
        const option = question.options.find(opt => opt.value === answerValue || opt.label === answerValue);
        return option ? option.label : answerValue;
      }

      if (question.question_type === 'file') {
        return answerValue;
      }

      return answerValue;
    },
    getQuestionTypeLabel(type) {
      const labels = {
        'text': 'Texto',
        'radio': 'Escolha Única',
        'checkbox': 'Múltipla Escolha',
        'file': 'Arquivo',
      };
      return labels[type] || type;
    },
    getQuestionTypeBadgeClass(type) {
      const classes = {
        'text': 'badge-light-primary',
        'radio': 'badge-light-info',
        'checkbox': 'badge-light-success',
        'file': 'badge-light-warning',
      };
      return classes[type] || 'badge-light-secondary';
    },
    formatDateTimeFull(dateTime) {
      if (!dateTime) {
        return '-';
      }
      return this.date(dateTime).format('DD/MM/YYYY HH:mm');
    },
    consultationRecordTitle(medicalRecord) {
      const schedule = resolveRecordSchedule(medicalRecord, this.schedule);
      return [medicalRecord.record_type?.rectype_name, schedule?.specialty?.spec_name].filter(Boolean).join(' · ');
    },
    historyRecordTitle(medicalRecord) {
      return medicalRecordHeaderTitle(
        medicalRecord,
        this.schedule,
        (dateTime) => this.date(dateTime).format('DD/MM/YYYY'),
      );
    },
    printMedicalRecord(medicalRecord) {
      this.printingMedrecId = medicalRecord.id_medrec;
      return this.$refs.medicalRecordPrinter.start(medicalRecord).finally(() => {
        this.printingMedrecId = null;
      });
    },
    onMedicalRecordPrinted(payload) {
      this.pdfSource = payload.pdfSource;
      this.pdfFileName = payload.pdfFileName;
      this.pdfSigned = payload.signed;
      this.$nextTick(() => showMedicalRecordPdf('modal_pdf_viewer_medical_record_history'));
    },
    specialtyPanelFor(medicalRecord) {
      return resolveSpecialtyPanel(this.schedule, medicalRecord, this.recordTypes);
    },
    isTeaRecordType(recordType) {
      const code = (recordType?.rectype_code ?? '').toLowerCase();
      if (code.startsWith('tea_')) {
        return true;
      }
      const name = (recordType?.rectype_name ?? '').toLowerCase();
      return name.includes('tea') || name.includes('aba') || name.includes('multiprofissional');
    },
    shouldUseOphthalmologyPanel(medicalRecord) {
      const specialtyName = this.schedule?.specialty?.spec_name?.toLowerCase?.() ?? '';
      if (!specialtyName.includes('oftalmologia')) {
        return false;
      }

      const isConsulta = (medicalRecord?.record_type?.rectype_name ?? '').toLowerCase() === 'consulta';
      if (!isConsulta) {
        return false;
      }

      const hasImageAnnotation = (medicalRecord?.attributes ?? []).some((attribute) => attribute.type === 'image_annotation');
      const recordType = (this.recordTypes ?? []).find((item) => item.id_rectype === medicalRecord.id_rectype);
      const useSpecialtyPanel = Boolean(recordType?.use_specialty_panel);

      return useSpecialtyPanel || hasImageAnnotation;
    },
  }
}
</script>

<style scoped>
.history-programs { padding: 16px; min-width: 0; }
.consultation-records .timeline-item { display: block; }
.consultation-records .timeline-card-wrapper { padding-bottom: 16px; }
.consultation-records .medical-record-card { margin-bottom: 0; box-shadow: none; border-color: #e9e6ed; }
.consultation-records .card-body-wrapper { border-top: 0; }
.consultation-records .card-body-wrapper > .card-body { padding: 20px; }
.consultation-content { display: flex; flex-direction: column; gap: 20px; }
.consultation-content > div > div { padding-left: 0 !important; padding-right: 0 !important; }
.consultation-content .content-card { border: 0; box-shadow: none; background: transparent; }
.consultation-content .modern-nav-header { padding: 0 0 12px; background: #fff; border-bottom: 1px solid #efedf2; }
.consultation-content .content-card > .card-body { padding: 16px 0 0 !important; }
.consultation-content .nav-btn { box-shadow: none; }
@media (max-width: 575px) {
  .consultation-records .card-body-wrapper > .card-body { padding: 16px; }
}

/* Reset do Splitter */
::v-deep(.p-splitter) {
  border: none !important;
  box-shadow: none !important;
}

/* Timeline vertical */
.history-timeline {
  position: relative;
  padding-left: 0;
}

.timeline-item {
  display: flex;
  position: relative;
  min-height: 60px;
}

.timeline-line-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 40px;
  flex-shrink: 0;
  position: relative;
  padding-top: 24px;
}

.timeline-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #9a9a9a;
  border: 3px solid #e8e8e8;
  z-index: 1;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.timeline-dot.active {
  background: #780f5a;
  border-color: rgba(120, 15, 90, 0.3);
  width: 20px;
  height: 20px;
  box-shadow: 0 0 0 4px rgba(120, 15, 90, 0.15);
}

.timeline-connector {
  width: 2px;
  flex: 1;
  background: linear-gradient(to bottom, #d0d0d0, #e8e8e8);
  margin-top: 4px;
}

.timeline-date-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 56px;
  flex-shrink: 0;
  padding-top: 16px;
  margin-right: 12px;
  text-align: center;
}

.date-day {
  font-size: 1.4rem;
  font-weight: 800;
  color: #780f5a;
  line-height: 1.1;
}

.date-month {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.date-year {
  font-size: 0.7rem;
  color: #adb5bd;
  font-weight: 500;
}

.timeline-card-wrapper {
  flex: 1;
  min-width: 0;
  padding-bottom: 20px;
}

/* Filtros wrapper */
.history-filters-card {
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.08);
  margin-bottom: 20px;
  padding: 16px 24px;
}

/* Card principal do registro médico */
.medical-record-card {
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.08);
  margin-bottom: 20px;
  transition: all 0.3s ease;
}

.medical-record-card:hover {
  box-shadow: 0 4px 16px rgba(120, 15, 90, 0.12);
}

/* Header moderno do card */
.card-header-modern {
  background: #780f5a;
  border: none;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s ease;
  border-radius: 12px 12px 0 0;
}

.card-header-modern:hover {
  background: #6a0d4e;
}

.card-header-modern.collapsed {
  background: #9a9a9a;
  border-radius: 12px;
}

.card-body-wrapper {
  border-top: 1px solid #e8e8e8;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.record-type-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.record-type-icon .svg-icon {
  color: white;
}

.record-header-info {
  min-width: 0;
}

.record-title {
  color: white;
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  word-break: break-word;
}

.record-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.record-meta {
  display: flex;
  align-items: center;
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.8rem;
}

.record-meta .symbol img.is-placeholder {
  filter: brightness(0) invert(1);
}

.header-actions {
  display: flex;
  gap: 6px;
  margin-left: 12px;
  margin-right: 8px;
  flex-shrink: 0;
}

.header-actions .btn {
  opacity: 0.9;
}

.header-actions .btn:hover {
  opacity: 1;
}

.card-toolbar {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 8px;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.card-toolbar:hover {
  background: rgba(255, 255, 255, 0.3);
}

.card-toolbar .svg-icon {
  color: white;
}

/* Card de conteúdo */
.content-card {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.08);
  transition: all 0.3s ease;
}

.content-card:hover {
  box-shadow: 0 4px 16px rgba(120, 15, 90, 0.12);
}

/* Header da navegação moderna */
.modern-nav-header {
  background: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
  padding: 16px 20px;
  border-radius: 12px 12px 0 0;
}

/* Navegação desktop */
.nav-desktop-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.nav-modern {
  display: flex;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.nav-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  cursor: pointer;
  background: #ffffff;
  color: #666666;
}

.nav-btn:hover {
  border-color: #780f5a;
  color: #780f5a;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.15);
}

.nav-btn-primary {
  background: #f8f9fa;
  color: #6c757d;
  border-color: #dee2e6;
}

.nav-btn-primary .nav-text {
  color: #6c757d;
}

.nav-btn-primary .nav-icon {
  color: #6c757d;
}

.nav-btn-primary:hover {
  background: #e9ecef;
  border-color: #780f5a;
  color: #780f5a;
}

.nav-btn-primary:hover .nav-text {
  color: #780f5a;
}

.nav-btn-primary:hover .nav-icon {
  color: #780f5a;
}

.nav-btn-primary.active,
.nav-btn-primary.active:focus,
.nav-btn-primary.active:hover {
  background: #780f5a !important;
  border-color: #780f5a !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.2);
}

.nav-btn-primary.active .nav-text {
  color: white !important;
}

.nav-btn-primary.active .nav-icon {
  color: white !important;
}

.nav-btn-secondary {
  background: #f8f9fa;
  color: #6c757d;
  border-color: #dee2e6;
}

.nav-btn-secondary.active,
.nav-btn-secondary.active:focus,
.nav-btn-secondary.active:hover {
  background: #780f5a !important;
  border-color: #780f5a !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.2);
}

.nav-btn-secondary.active .nav-text {
  color: white !important;
}

.nav-btn-secondary.active .nav-icon {
  color: white !important;
}

.nav-btn-secondary .nav-text {
  color: #6c757d;
}

.nav-btn-secondary .nav-icon {
  color: #6c757d;
}

.nav-btn-secondary:hover {
  background: #e9ecef;
  border-color: #780f5a;
  color: #780f5a;
}

.nav-btn-secondary:hover .nav-text {
  color: #780f5a;
}

.nav-btn-secondary:hover .nav-icon {
  color: #780f5a;
}

.nav-btn-success {
  background: #f8f9fa;
  color: #6c757d;
  border-color: #dee2e6;
}

.nav-btn-success.active,
.nav-btn-success.active:focus,
.nav-btn-success.active:hover {
  background: #780f5a !important;
  border-color: #780f5a !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.2);
}

.nav-btn-success.active .nav-text {
  color: white !important;
}

.nav-btn-success.active .nav-icon {
  color: white !important;
}

.nav-btn-success .nav-text {
  color: #6c757d;
}

.nav-btn-success .nav-icon {
  color: #6c757d;
}

.nav-btn-success:hover {
  background: #e9ecef;
  border-color: #780f5a;
  color: #780f5a;
}

.nav-btn-success:hover .nav-text {
  color: #780f5a;
}

.nav-btn-success:hover .nav-icon {
  color: #780f5a;
}

.nav-btn-info {
  background: #f8f9fa;
  color: #6c757d;
  border-color: #dee2e6;
}

.nav-btn-info.active,
.nav-btn-info.active:focus,
.nav-btn-info.active:hover {
  background: #780f5a !important;
  border-color: #780f5a !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.2);
}

.nav-btn-info.active .nav-text {
  color: white !important;
}

.nav-btn-info.active .nav-icon {
  color: white !important;
}

.nav-btn-info .nav-text {
  color: #6c757d;
}

.nav-btn-info .nav-icon {
  color: #6c757d;
}

.nav-btn-info:hover {
  background: #e9ecef;
  border-color: #780f5a;
  color: #780f5a;
}

.nav-btn-info:hover .nav-text {
  color: #780f5a;
}

.nav-btn-info:hover .nav-icon {
  color: #780f5a;
}

.nav-btn-warning {
  background: #f8f9fa;
  color: #6c757d;
  border-color: #dee2e6;
}

.nav-btn-warning.active,
.nav-btn-warning.active:focus,
.nav-btn-warning.active:hover {
  background: #780f5a !important;
  border-color: #780f5a !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.2);
}

.nav-btn-warning.active .nav-text {
  color: white !important;
}

.nav-btn-warning.active .nav-icon {
  color: white !important;
}

.nav-btn-warning .nav-text {
  color: #6c757d;
}

.nav-btn-warning .nav-icon {
  color: #6c757d;
}

.nav-btn-warning:hover {
  background: #e9ecef;
  border-color: #780f5a;
  color: #780f5a;
}

.nav-btn-warning:hover .nav-text {
  color: #780f5a;
}

.nav-btn-warning:hover .nav-icon {
  color: #780f5a;
}

.nav-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-text {
  font-weight: 600;
}

/* Navegação mobile */
.nav-mobile {
  display: none;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.nav-modern-mobile {
  display: flex;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
  flex-wrap: wrap;
}

.nav-btn-mobile {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.8rem;
  transition: all 0.2s ease;
  cursor: pointer;
  background: #ffffff;
  color: #666666;
}

.nav-btn-mobile:hover {
  border-color: #780f5a;
  color: #780f5a;
  box-shadow: 0 2px 6px rgba(120, 15, 90, 0.15);
}

.nav-icon-mobile {
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-text-mobile {
  font-weight: 600;
}

/* Estados ativos para mobile */
.nav-btn-mobile.active {
  background: #780f5a !important;
  border-color: #780f5a !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.2);
}

.nav-btn-mobile.active .nav-text-mobile {
  color: white !important;
}

.nav-btn-mobile.active .nav-icon-mobile {
  color: white !important;
}

/* Dropdown moderno */
.modern-dropdown {
  position: relative;
}

.dropdown-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 7px 12px;
  color: #6c757d;
  transition: all 0.2s ease;
  cursor: pointer;
}

.dropdown-btn:hover {
  background: #e9ecef;
  border-color: #780f5a;
  color: #780f5a;
  box-shadow: 0 2px 6px rgba(120, 15, 90, 0.15);
}

.dropdown-btn.active {
  background: #780f5a;
  border-color: #780f5a;
  color: white;
}

.modern-dropdown-item.active {
  background: rgba(120, 15, 90, 0.08);
  color: #780f5a;
}

.dropdown-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.modern-dropdown-menu {
  background: white;
  border: 1px solid #e3e6f0;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  padding: 8px;
  margin-top: 8px;
  min-width: 180px;
}

.modern-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border: none;
  background: none;
  border-radius: 8px;
  color: #495057;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  cursor: pointer;
  width: 100%;
  text-align: left;
}

.modern-dropdown-item:hover {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  color: #212529;
}

.dropdown-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
}

.dropdown-item-text {
  font-weight: 600;
}

/* Card CID */
.cid-card {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.08);
  margin-top: 16px;
  transition: all 0.3s ease;
}

.cid-card:hover {
  box-shadow: 0 4px 16px rgba(120, 15, 90, 0.12);
}

.cid-header {
  background: #780f5a;
  padding: 16px 20px;
  border-radius: 12px 12px 0 0;
}

.cid-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cid-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.cid-icon .svg-icon {
  color: white;
}

.cid-title-text {
  color: white;
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.cid-body {
  padding: 10px;
  overflow: visible !important;
  max-height: none !important;
  min-height: 200px;
  display: flex;
  flex-direction: column;
}

.cid-card .card {
  overflow: visible !important;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.cid-card .card-body {
  overflow: visible !important;
  max-height: none !important;
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Timeline responsividade */
@media (max-width: 767.98px) {
  .timeline-date-label {
    display: none;
  }
  .timeline-line-wrapper {
    width: 30px;
  }
}

/* Responsividade */
@media (max-width: 1399.98px) {
  .nav-desktop-group {
    display: none !important;
  }
  .nav-mobile {
    display: flex !important;
  }
}

@media (min-width: 1400px) {
  .nav-desktop-group {
    display: flex !important;
  }
  .nav-mobile {
    display: none !important;
  }

  .nav-modern {
    flex-wrap: nowrap;
  }
}

.modal {
  z-index: 1055 !important;
}

.modal-backdrop {
  z-index: 1050 !important;
}

/* Animações */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.medical-record-card {
  animation: fadeInUp 0.6s ease-out;
}
</style>
